import { apiClient } from '@/common/lib/api-client';

const quotationsBase = (companyId) =>
  `/v1/whatsapp/companies/${companyId}/quotations`;

const customerQuotationsBase = (companyId) =>
  `/v1/whatsapp/companies/${companyId}/customer-quotations`;

const quotationPdfTemplatesBase = (companyId) =>
  `/v1/whatsapp/companies/${companyId}/quotation-templates`;

/** When responseType is blob, 4xx bodies arrive as Blob — parse JSON for callers. */
async function hydrateBlobError(err) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      err.response.data = JSON.parse(await data.text());
    } catch {
      // leave original blob body
    }
  }
  return err;
}

/**
 * List PDF design templates (dynamic catalog).
 * GET /v1/whatsapp/companies/{company_id}/quotation-templates/
 */
export async function getQuotationPdfTemplates(companyId, params = {}) {
  const { data } = await apiClient.get(
    `${quotationPdfTemplatesBase(companyId)}/`,
    { params },
  );
  return data;
}

/**
 * Stream live sample PDF for a design template.
 * GET /v1/whatsapp/companies/{company_id}/quotation-templates/{template_key}/preview-pdf/
 */
export async function getQuotationTemplatePreviewPdf({
  companyId,
  templateKey,
}) {
  try {
    const { data } = await apiClient.get(
      `${quotationPdfTemplatesBase(companyId)}/${templateKey}/preview-pdf/`,
      { responseType: 'blob' },
    );
    return data;
  } catch (err) {
    throw await hydrateBlobError(err);
  }
}

/**
 * List quotation templates.
 * GET /v1/whatsapp/companies/{company_id}/quotations/
 */
export async function getQuotations(companyId, params = {}) {
  const { data } = await apiClient.get(`${quotationsBase(companyId)}/`, {
    params,
  });
  return data;
}

/**
 * Create quotation template.
 * POST /v1/whatsapp/companies/{company_id}/quotations/
 */
export async function createQuotation({ companyId, payload }) {
  const { data } = await apiClient.post(
    `${quotationsBase(companyId)}/`,
    payload,
  );
  return data;
}

/**
 * Retrieve quotation template (includes nested products).
 * GET /v1/whatsapp/companies/{company_id}/quotations/{quotation_id}/
 */
export async function getQuotation({ companyId, quotationId }) {
  const { data } = await apiClient.get(
    `${quotationsBase(companyId)}/${quotationId}/`,
  );
  return data;
}

/**
 * Update quotation template.
 * PUT /v1/whatsapp/companies/{company_id}/quotations/{quotation_id}/
 */
export async function updateQuotation({ companyId, quotationId, payload }) {
  const { data } = await apiClient.put(
    `${quotationsBase(companyId)}/${quotationId}/`,
    payload,
  );
  return data;
}

/**
 * Delete quotation template.
 * DELETE /v1/whatsapp/companies/{company_id}/quotations/{quotation_id}/
 */
export async function deleteQuotation({ companyId, quotationId }) {
  await apiClient.delete(`${quotationsBase(companyId)}/${quotationId}/`);
}

/**
 * List products for a quotation.
 * GET /v1/whatsapp/companies/{company_id}/quotations/{quotation_id}/products/
 */
export async function getQuotationProducts({
  companyId,
  quotationId,
  params = {},
}) {
  const { data } = await apiClient.get(
    `${quotationsBase(companyId)}/${quotationId}/products/`,
    { params },
  );
  return data;
}

/**
 * Retrieve single quotation product.
 * GET /v1/whatsapp/companies/{company_id}/quotations/{quotation_id}/products/{product_id}/
 */
export async function getQuotationProduct({
  companyId,
  quotationId,
  productId,
}) {
  const { data } = await apiClient.get(
    `${quotationsBase(companyId)}/${quotationId}/products/${productId}/`,
  );
  return data;
}

function appendPayloadToFormData(formData, payload) {
  Object.entries(payload).forEach(([key, val]) => {
    if (val === undefined || val === null) return;
    formData.append(key, typeof val === 'boolean' ? String(val) : val);
  });
}

/**
 * Create quotation product. Multipart when imageFile is set.
 * POST /v1/whatsapp/companies/{company_id}/quotations/{quotation_id}/products/
 */
export async function createQuotationProduct({
  companyId,
  quotationId,
  payload,
  imageFile,
}) {
  if (imageFile) {
    const formData = new FormData();
    appendPayloadToFormData(formData, payload);
    formData.append('image', imageFile);
    const { data } = await apiClient.post(
      `${quotationsBase(companyId)}/${quotationId}/products/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  }
  const { data } = await apiClient.post(
    `${quotationsBase(companyId)}/${quotationId}/products/`,
    payload,
  );
  return data;
}

/**
 * Update quotation product. Multipart when imageFile is set.
 * PUT /v1/whatsapp/companies/{company_id}/quotations/{quotation_id}/products/{product_id}/
 */
export async function updateQuotationProduct({
  companyId,
  quotationId,
  productId,
  payload,
  imageFile,
}) {
  if (imageFile) {
    const formData = new FormData();
    appendPayloadToFormData(formData, payload);
    formData.append('image', imageFile);
    const { data } = await apiClient.put(
      `${quotationsBase(companyId)}/${quotationId}/products/${productId}/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  }
  const { data } = await apiClient.put(
    `${quotationsBase(companyId)}/${quotationId}/products/${productId}/`,
    payload,
  );
  return data;
}

/**
 * Delete quotation product.
 * DELETE /v1/whatsapp/companies/{company_id}/quotations/{quotation_id}/products/{product_id}/
 */
export async function deleteQuotationProduct({
  companyId,
  quotationId,
  productId,
}) {
  await apiClient.delete(
    `${quotationsBase(companyId)}/${quotationId}/products/${productId}/`,
  );
}

/**
 * Quotation / product activity timeline.
 * GET /v1/whatsapp/companies/{company_id}/quotations/{quotation_id}/activities/
 * @param {object} args
 * @param {string|number} args.companyId
 * @param {string|number} args.quotationId
 * @param {object} [args.params] - product_id, action_type, page, page_size
 */
export async function getQuotationActivities({
  companyId,
  quotationId,
  params = {},
}) {
  const { data } = await apiClient.get(
    `${quotationsBase(companyId)}/${quotationId}/activities/`,
    { params },
  );
  return data;
}

/**
 * Preview/download catalog PDF (file stream).
 * GET /v1/whatsapp/companies/{company_id}/quotations/{quotation_id}/pdf/
 */
export async function downloadQuotationPdf({ companyId, quotationId }) {
  try {
    const response = await apiClient.get(
      `${quotationsBase(companyId)}/${quotationId}/pdf/`,
      { responseType: 'blob' },
    );
    return response.data;
  } catch (err) {
    throw await hydrateBlobError(err);
  }
}

/**
 * Force rebuild catalog PDF.
 * POST /v1/whatsapp/companies/{company_id}/quotations/{quotation_id}/pdf/regenerate/
 */
export async function regenerateQuotationPdf({ companyId, quotationId }) {
  const { data } = await apiClient.post(
    `${quotationsBase(companyId)}/${quotationId}/pdf/regenerate/`,
  );
  return data;
}

/**
 * List customer quotations generated by flows.
 * GET /v1/whatsapp/companies/{company_id}/customer-quotations/
 */
export async function getCustomerQuotations(companyId, params = {}) {
  const { data } = await apiClient.get(
    `${customerQuotationsBase(companyId)}/`,
    { params },
  );
  return data;
}

/**
 * Retrieve customer quotation with line items.
 * GET /v1/whatsapp/companies/{company_id}/customer-quotations/{id}/
 */
export async function getCustomerQuotation({ companyId, customerQuotationId }) {
  const { data } = await apiClient.get(
    `${customerQuotationsBase(companyId)}/${customerQuotationId}/`,
  );
  return data;
}

/**
 * Create a customer quotation manually (calculates totals + generates PDF).
 * POST /v1/whatsapp/companies/{company_id}/customer-quotations/
 *
 * payload: { quotation_id, customer_name, customer_phone, customer_email,
 *            tax_rate, notes, items: [{ product_id } | { product_name, unit_price, moq }] }
 */
export async function createCustomerQuotation({ companyId, payload }) {
  const { data } = await apiClient.post(
    `${customerQuotationsBase(companyId)}/`,
    payload,
  );
  return data;
}

/**
 * Send the generated PDF to the customer via WhatsApp.
 * POST /v1/whatsapp/companies/{company_id}/customer-quotations/{id}/send/
 *
 * payload: { customer_phone?, message_text? }  — both optional
 */
export async function sendCustomerQuotation({
  companyId,
  customerQuotationId,
  payload = {},
}) {
  const { data } = await apiClient.post(
    `${customerQuotationsBase(companyId)}/${customerQuotationId}/send/`,
    payload,
  );
  return data;
}

/**
 * Recalculate totals and regenerate the PDF (optional template switch).
 * Prefers POST .../regenerate-pdf/ (new). Falls back to .../pdf/regenerate/.
 *
 * payload: { template_key? }
 */
export async function regenerateCustomerQuotationPdf({
  companyId,
  customerQuotationId,
  payload = {},
}) {
  const body = payload && typeof payload === 'object' ? payload : {};
  const base = `${customerQuotationsBase(companyId)}/${customerQuotationId}`;
  const { data } = await apiClient.post(`${base}/pdf/regenerate/`, body);
  return data;
}

/**
 * Delete a customer quotation.
 * DELETE /v1/whatsapp/companies/{company_id}/customer-quotations/{id}/
 */
export async function deleteCustomerQuotation({
  companyId,
  customerQuotationId,
}) {
  await apiClient.delete(
    `${customerQuotationsBase(companyId)}/${customerQuotationId}/`,
  );
}
